import React, { useCallback, useEffect, useRef, useState } from 'react'
import whatsappApi from './whatsappApi'
import myToast from '../../Utils/MyToast'

const QUALITY_COLORS = {
  GREEN: { bg: '#dcfce7', color: '#166534' },
  YELLOW: { bg: '#fef9c3', color: '#854d0e' },
  RED: { bg: '#fee2e2', color: '#991b1b' },
}

const WA = '#25D366'
const WA_DARK = '#128C7E'

// Created By Cursor
function loadFacebookSdk(appId, graphVersion) {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB)
      return
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: graphVersion || 'v20.0',
      })
      resolve(window.FB)
    }
    if (document.getElementById('facebook-jssdk')) {
      return
    }
    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.async = true
    script.defer = true
    script.crossOrigin = 'anonymous'
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.onerror = () => reject(new Error('Failed to load Facebook SDK'))
    document.body.appendChild(script)
  })
}

const WhatsAppConnectionCard = ({
  account,
  onConnected,
  onAccountSynced,
  loadingAccount,
}) => {
  const [busy, setBusy] = useState(false)
  const [initConfig, setInitConfig] = useState(null)
  const [checking, setChecking] = useState(false)
  const [live, setLive] = useState(null)
  const [liveAccount, setLiveAccount] = useState(null)
  const sessionRef = useRef({})
  const onConnectedRef = useRef(onConnected)
  const onAccountSyncedRef = useRef(onAccountSynced)
  const accountRef = useRef(account)
  const checkInFlightRef = useRef(false)
  const autoCheckedRef = useRef(false)

  useEffect(() => {
    onConnectedRef.current = onConnected
  }, [onConnected])

  useEffect(() => {
    onAccountSyncedRef.current = onAccountSynced
  }, [onAccountSynced])

  useEffect(() => {
    accountRef.current = account
  }, [account])

  // Created By Cursor — ask Meta if phone number is still connected (stable; no re-check loop)
  const checkPhoneConnection = useCallback(async (silent = true) => {
    if (checkInFlightRef.current) return null
    checkInFlightRef.current = true
    setChecking(true)
    try {
      const res = await whatsappApi.checkConnection()
      if (res.api_status === 'OK' && res.data) {
        setLive(res.data)
        if (res.data.account) {
          setLiveAccount(res.data.account)
          if (onAccountSyncedRef.current) {
            onAccountSyncedRef.current(res.data.account)
          }
        }
        if (!silent && onConnectedRef.current && res.data.account) {
          onConnectedRef.current({
            account: res.data.account,
            connection_check: res.data,
          })
        }
        if (!silent) {
          if (res.data.phone_connected) {
            myToast.notify_success(res.data.message || 'Phone number is connected')
          } else {
            myToast.notify_error(res.data.message || 'Phone number is not connected')
          }
        }
        return res.data
      }
      setLive({
        phone_connected: false,
        message: res.api_remark || 'Unable to verify phone connection',
      })
      if (!silent) {
        myToast.notify_error(res.api_remark || 'Unable to verify phone connection')
      }
      return null
    } catch (err) {
      const saved = accountRef.current
      const msg =
        err?.code === 'ECONNABORTED'
          ? 'Connection check timed out. Showing saved status.'
          : err?.message || 'Unable to verify phone connection'
      setLive({
        phone_connected: !!(saved && saved.connection_status === 'connected'),
        message: msg,
        timed_out: err?.code === 'ECONNABORTED',
      })
      if (!silent) {
        myToast.notify_error(msg)
      }
      return null
    } finally {
      checkInFlightRef.current = false
      setChecking(false)
    }
  }, [])

  // Do NOT auto-hit Meta on every visit — trust DB after connect/register.
  // Manual "Check phone status" still available when troubleshooting.
  useEffect(() => {
    if (loadingAccount) return
    // no auto Meta verify
  }, [loadingAccount])

  useEffect(() => {
    const onMessage = (event) => {
      if (!event.origin || !event.origin.includes('facebook.com')) return
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        if (data?.type !== 'WA_EMBEDDED_SIGNUP') return
        if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA') {
          sessionRef.current = {
            ...sessionRef.current,
            waba_id: data.data?.waba_id || sessionRef.current.waba_id,
            phone_number_id: data.data?.phone_number_id || sessionRef.current.phone_number_id,
            business_id: data.data?.business_id || sessionRef.current.business_id,
          }
        }
      } catch (e) {
        /* ignore non-JSON */
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const prepareSdk = useCallback(async () => {
    const res = await whatsappApi.onboardInit()
    if (res.api_status !== 'OK') {
      throw new Error(res.api_remark || 'Failed to load onboard config')
    }
    setInitConfig(res.data)
    await loadFacebookSdk(res.data.app_id, res.data.graph_version)
    return res.data
  }, [])

  const launchSignup = async () => {
    setBusy(true)
    try {
      const config = initConfig || (await prepareSdk())
      if (!window.FB) {
        myToast.notify_error('Facebook SDK not ready')
        setBusy(false)
        return
      }
      if (!config.app_id || config.app_id === 'YOUR_META_APP_ID') {
        myToast.notify_error('Meta App ID not configured on server (META_WA_APP_ID)')
        setBusy(false)
        return
      }

      window.FB.login(
        function (response) {
          // Facebook SDK requires a plain function (not async) as the FB.login callback
          // Created By Cursor
          const finishOnboarding = async () => {
            try {
              if (!response.authResponse) {
                myToast.notify_error('WhatsApp signup cancelled or failed')
                return
              }
              const code = response.authResponse.code
              // Give postMessage events a brief moment to land
              await new Promise((resolve) => setTimeout(resolve, 400))
              const payload = {
                code,
                waba_id: sessionRef.current.waba_id,
                phone_number_id: sessionRef.current.phone_number_id,
                business_id: sessionRef.current.business_id,
              }
              if (!payload.waba_id || !payload.phone_number_id) {
                myToast.notify_error('Missing WABA details from Meta popup. Try again.')
                return
              }
              const cb = await whatsappApi.onboardCallback(payload)
              if (cb.api_status === 'OK') {
                myToast.notify_success('WhatsApp Business Account connected')
                if (cb.data?.warning) {
                  myToast.notify_error(cb.data.warning)
                }
                if (onConnectedRef.current) onConnectedRef.current(cb.data)
                if (cb.data?.account) {
                  setLiveAccount(cb.data.account)
                  setLive({
                    phone_connected: cb.data?.phone_registered !== false,
                    connection_status: cb.data?.account?.connection_status,
                    message: cb.data?.warning || 'Account saved',
                    account: cb.data.account,
                  })
                }
              } else {
                myToast.notify_error(cb.api_remark || 'Onboarding callback failed')
              }
            } catch (err) {
              myToast.notify_error(err.message || 'Onboarding error')
            } finally {
              setBusy(false)
            }
          }
          finishOnboarding()
        },
        {
          config_id: config.login_config_id,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: config.setup || {},
            featureType: '',
            sessionInfoVersion: '3',
            version: 'v4',
          },
        }
      )
    } catch (err) {
      myToast.notify_error(err.message || 'Unable to start WhatsApp signup')
      setBusy(false)
    }
  }

  // Created By Cursor — Cloud API /register for already-linked numbers (#133010)
  const registerPhone = async () => {
    setBusy(true)
    try {
      const res = await whatsappApi.registerPhone()
      if (res.api_status === 'OK' && res.data?.account) {
        setLiveAccount(res.data.account)
        setLive({
          phone_connected: true,
          connection_status: 'connected',
          message: res.api_remark || 'Phone registered on Meta Cloud API',
          account: res.data.account,
        })
        if (onAccountSyncedRef.current) onAccountSyncedRef.current(res.data.account)
        myToast.notify_success(res.api_remark || 'Phone registered')
      } else {
        if (res.data?.account) {
          setLiveAccount(res.data.account)
          if (onAccountSyncedRef.current) onAccountSyncedRef.current(res.data.account)
        }
        myToast.notify_error(res.api_remark || 'Register failed')
      }
    } catch (err) {
      myToast.notify_error(err.message || 'Register failed')
    } finally {
      setBusy(false)
    }
  }

  const displayAccount = liveAccount || account
  const hasLinkedAccount = !!(
    displayAccount &&
    (displayAccount.phone_number_id || displayAccount.waba_id || displayAccount.display_phone_number)
  )
  const connStatus = String(
    live?.connection_status || displayAccount?.connection_status || ''
  ).toLowerCase()
  const dbConnected = connStatus === 'connected'
  const rateLimited = !!(live?.rate_limited || live?.meta?.rate_limited)
  const notRegistered = connStatus === 'not_registered'
  // Prefer live Meta result once available; otherwise trust DB (no forced Meta check)
  const phoneConnected =
    live != null ? !!live.phone_connected : dbConnected
  const needsReconnect =
    hasLinkedAccount &&
    !phoneConnected &&
    (connStatus === 'token_error' ||
      connStatus === 'disabled' ||
      connStatus === 'pending' ||
      connStatus === 'not_registered')
  const quality = (displayAccount?.quality_rating || live?.meta?.quality_rating || '').toUpperCase()
  const qStyle = QUALITY_COLORS[quality] || { bg: '#e5e7eb', color: '#374151' }
  const displayPhone =
    live?.meta?.display_phone_number ||
    displayAccount?.display_phone_number ||
    '—'
  const verifiedName =
    live?.meta?.verified_name || displayAccount?.verified_name || 'WhatsApp Business'
  const codeStatus = live?.code_verification_status || live?.meta?.code_verification_status

  const bannerTone = phoneConnected ? 'ok' : 'warn'
  const bannerTitle = phoneConnected
    ? 'Phone number is connected'
    : hasLinkedAccount
      ? rateLimited
        ? 'Account linked — Meta check rate-limited'
        : notRegistered
          ? 'Account linked — phone not registered on Cloud API'
          : connStatus === 'token_error'
            ? 'Account linked — authorization issue'
            : 'Account linked — not verified with Meta'
      : 'Phone number is not connected'
  // Avoid repeating phone when the phone chip is shown
  const bannerSub = phoneConnected
    ? live?.message && !String(live.message).includes(String(displayPhone))
      ? live.message
      : `${verifiedName} · ready to message parents`
    : live?.message ||
      (hasLinkedAccount
        ? notRegistered
          ? 'Send fails with (#133010). Click Register Phone on Meta Cloud API.'
          : verifiedName
        : 'Link or reconnect your WhatsApp Business number')

  return (
    <div style={{ padding: '4px 0 8px', width: '100%' }}>
      <div
        style={{
          marginBottom: 14,
          padding: '16px 18px',
          borderRadius: 14,
          border: '1px solid #86efac',
          background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 40%, #fff 100%)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 11,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
                  color: '#fff',
                  boxShadow: '0 4px 10px rgba(18, 140, 126, 0.28)',
                  flexShrink: 0,
                }}
              >
                <i className="fa fa-whatsapp" style={{ fontSize: 16 }} />
              </span>
              <div style={{ fontSize: 15, fontWeight: 800, color: WA_DARK }}>
                Connection
              </div>
            </div>
            <p
              style={{
                color: '#64748b',
                margin: 0,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              Connect your own WhatsApp Business number so parents receive fee
              reminders and notices directly from your school — not from EduOK.
            </p>
          </div>
          <button
            type="button"
            disabled={checking || loadingAccount || busy}
            onClick={() => checkPhoneConnection(false)}
            style={{
              border: `1px solid ${WA_DARK}`,
              background: '#fff',
              color: WA_DARK,
              fontWeight: 800,
              fontSize: 12,
              padding: '10px 14px',
              borderRadius: 10,
              cursor: checking || loadingAccount || busy ? 'not-allowed' : 'pointer',
              opacity: checking || loadingAccount || busy ? 0.65 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0,
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
            }}
          >
            <i
              className={`fa fa-refresh${checking ? ' fa-spin' : ''}`}
              style={{ fontSize: 12 }}
            />
            {checking ? 'Checking…' : 'Check phone status'}
          </button>
        </div>

        {/* Phone connection status strip */}
        {!loadingAccount ? (
          <div
            style={{
              marginTop: 14,
              padding: '12px 14px',
              borderRadius: 12,
              border:
                bannerTone === 'ok' ? '1px solid #86efac' : '1px solid #fcd34d',
              background:
                bannerTone === 'ok'
                  ? 'linear-gradient(135deg, #fff 0%, #f0fdf4 100%)'
                  : 'linear-gradient(135deg, #fff 0%, #fffbeb 100%)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: phoneConnected
                  ? `linear-gradient(135deg, ${WA_DARK}, ${WA})`
                  : 'linear-gradient(135deg, #d97706, #f59e0b)',
                color: '#fff',
                flexShrink: 0,
                boxShadow: phoneConnected
                  ? '0 4px 10px rgba(37, 211, 102, 0.28)'
                  : '0 4px 10px rgba(245, 158, 11, 0.25)',
              }}
            >
              <i
                className={`fa ${
                  checking
                    ? 'fa-spinner fa-spin'
                    : phoneConnected
                      ? 'fa-check'
                      : 'fa-exclamation'
                }`}
              />
            </span>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 14,
                  color: phoneConnected ? '#166534' : '#92400e',
                }}
              >
                {bannerTitle}
                {checking ? (
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 12,
                      color: '#64748b',
                      marginLeft: 8,
                    }}
                  >
                    (verifying…)
                  </span>
                ) : null}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#64748b',
                  marginTop: 3,
                  fontWeight: 600,
                  lineHeight: 1.35,
                }}
              >
                {bannerSub}
              </div>
            </div>
            {displayPhone && displayPhone !== '—' ? (
              <span
                style={{
                  fontWeight: 800,
                  fontSize: 13,
                  color: phoneConnected ? WA_DARK : '#92400e',
                  background: phoneConnected ? '#ecfdf5' : '#fef3c7',
                  border: phoneConnected ? '1px solid #86efac' : '1px solid #fde68a',
                  borderRadius: 999,
                  padding: '7px 12px',
                  fontVariantNumeric: 'tabular-nums',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
              >
                <i className="fa fa-phone" style={{ marginRight: 7 }} />
                {displayPhone}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {loadingAccount ? (
        <div className="text-muted">Loading connection status…</div>
      ) : !hasLinkedAccount ? (
        <div
          style={{
            border: '1px dashed #86efac',
            background: '#f0fdf4',
            borderRadius: 12,
            padding: 20,
            width: '100%',
          }}
        >
          <div style={{ fontWeight: 700, color: '#166534', marginBottom: 8 }}>
            Not Connected
          </div>
          <p style={{ color: '#374151', fontSize: 14, marginBottom: 16 }}>
            You will create or link a WhatsApp Business Account under your school&apos;s
            Meta Business Portfolio via Meta Embedded Signup.
          </p>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={launchSignup}
            style={{
              background: '#25D366',
              color: '#fff',
              fontWeight: 600,
              borderRadius: 8,
              padding: '10px 18px',
            }}
          >
            <i className="fa fa-whatsapp" style={{ marginRight: 8 }} />
            {busy ? 'Connecting…' : 'Connect WhatsApp Business Account'}
          </button>
        </div>
      ) : (
        <div
          style={{
            border: phoneConnected ? '1px solid #86efac' : '1px solid #fcd34d',
            background: phoneConnected
              ? 'linear-gradient(135deg, #ecfdf5 0%, #fff 48%, #f8fafc 100%)'
              : 'linear-gradient(135deg, #fffbeb 0%, #fff 55%, #f8fafc 100%)',
            borderRadius: 14,
            padding: 18,
            width: '100%',
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}
          >
            <StatusPill
              bg={phoneConnected ? '#dcfce7' : '#fef3c7'}
              color={phoneConnected ? '#166534' : '#92400e'}
              border={phoneConnected ? '1px solid #86efac' : '1px solid #fde68a'}
            >
              {phoneConnected ? 'Connected' : 'Linked'}
            </StatusPill>
            {rateLimited ? (
              <StatusPill bg="#ffedd5" color="#9a3412" border="1px solid #fdba74">
                Meta rate limited
              </StatusPill>
            ) : null}
            {connStatus === 'not_registered' ? (
              <StatusPill bg="#fee2e2" color="#991b1b" border="1px solid #fca5a5">
                Not registered
              </StatusPill>
            ) : null}
            {connStatus === 'token_error' ? (
              <StatusPill bg="#fee2e2" color="#991b1b" border="1px solid #fca5a5">
                Auth error
              </StatusPill>
            ) : null}
            {codeStatus ? (
              <StatusPill
                bg={codeStatus === 'VERIFIED' ? '#dcfce7' : '#fef9c3'}
                color={codeStatus === 'VERIFIED' ? '#166534' : '#854d0e'}
                border={
                  codeStatus === 'VERIFIED' ? '1px solid #86efac' : '1px solid #fde68a'
                }
              >
                Verification · {prettyMetaLabel(codeStatus)}
              </StatusPill>
            ) : null}
            {quality ? (
              <StatusPill bg={qStyle.bg} color={qStyle.color} border="1px solid rgba(15,23,42,0.06)">
                Quality · {prettyMetaLabel(quality)}
              </StatusPill>
            ) : null}
            {displayAccount?.messaging_tier || live?.meta?.messaging_tier ? (
              <StatusPill bg="#f1f5f9" color="#475569" border="1px solid #e2e8f0">
                Tier ·{' '}
                {prettyMetaLabel(
                  displayAccount?.messaging_tier || live?.meta?.messaging_tier
                )}
              </StatusPill>
            ) : null}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 16,
                color: '#fff',
                background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
                boxShadow: '0 6px 14px rgba(18, 140, 126, 0.28)',
              }}
            >
              {accountInitials(verifiedName)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: '#0f172a',
                  lineHeight: 1.2,
                }}
              >
                {verifiedName}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 14,
                  fontWeight: 700,
                  color: WA_DARK,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {displayPhone}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 10,
              width: '100%',
            }}
          >
            <InfoTile label="WABA ID" value={displayAccount?.waba_id || '—'} mono />
            <InfoTile
              label="Phone Number ID"
              value={displayAccount?.phone_number_id || '—'}
              mono
            />
            <InfoTile
              label="Onboarded"
              value={formatOnboarded(displayAccount?.onboarded_at)}
            />
            <InfoTile
              label="Status"
              value={prettyMetaLabel(connStatus || '—')}
              emphasize={phoneConnected}
            />
          </div>

          {(needsReconnect || quality === 'RED' || rateLimited || notRegistered) && (
            <div
              style={{
                marginTop: 14,
                marginBottom: 0,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.45,
                padding: '12px 14px',
                borderRadius: 12,
                background: '#fffbeb',
                border: '1px solid #fde68a',
                color: '#92400e',
              }}
            >
              {notRegistered
                ? 'Meta (#133010): this phone is linked in EduOK but not registered on Cloud API. Click Register Phone once — then messaging will work. Status is stored in the database; you do not need to verify Meta on every visit.'
                : rateLimited
                  ? 'Meta temporarily rate-limited this WABA (#80008). Your number is still linked in EduOK — wait a few minutes, then use Check phone status.'
                  : quality === 'RED'
                    ? 'Quality rating is RED — Meta may restrict messaging. Stop sending to opted-out numbers and review template content.'
                    : connStatus === 'token_error'
                      ? 'Meta could not authorize this phone with the current system token. Reconnect / Re-authorize to refresh access.'
                      : 'Phone is linked in EduOK but not currently verified as connected with Meta. Reconnect if messaging fails.'}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
            {(notRegistered || !phoneConnected) && hasLinkedAccount ? (
              <button
                type="button"
                disabled={busy}
                onClick={registerPhone}
                style={connBtnPrimary}
              >
                {busy ? 'Registering…' : 'Register Phone (fix #133010)'}
              </button>
            ) : null}
            <button
              type="button"
              disabled={busy}
              onClick={launchSignup}
              style={connBtnSecondary}
            >
              {phoneConnected ? 'Reconnect / Re-authorize' : 'Reconnect WhatsApp'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Created By Cursor
function prettyMetaLabel(value) {
  const raw = String(value || '').trim()
  if (!raw || raw === '—') return '—'
  if (raw === 'NOT_APPLICABLE') return 'Not applicable'
  if (raw === 'UNKNOWN') return 'Unknown'
  if (raw === 'connected') return 'Connected'
  if (raw === 'not_registered') return 'Not registered'
  if (raw === 'token_error') return 'Auth error'
  return raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// Created By Cursor
function accountInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return 'WA'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Created By Cursor
function formatOnboarded(value) {
  if (!value) return '—'
  const s = String(value).trim()
  if (!s) return '—'
  try {
    const d = new Date(s.replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return s
    return d.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (e) {
    return s
  }
}

// Created By Cursor
function StatusPill({ children, bg, color, border }) {
  return (
    <span
      style={{
        background: bg,
        color,
        border: border || '1px solid transparent',
        fontWeight: 800,
        fontSize: 11,
        padding: '5px 11px',
        borderRadius: 999,
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </span>
  )
}

// Created By Cursor
function InfoTile({ label, value, mono, emphasize }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 12,
        background: '#fff',
        border: emphasize ? '1px solid #86efac' : '1px solid #e2e8f0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: '#94a3b8',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: emphasize ? WA_DARK : '#0f172a',
          wordBreak: 'break-all',
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
          fontFamily: mono
            ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
            : undefined,
        }}
      >
        {value}
      </div>
    </div>
  )
}

const connBtnPrimary = {
  border: 'none',
  background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
  color: '#fff',
  fontWeight: 800,
  fontSize: 13,
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(37, 211, 102, 0.25)',
}

const connBtnSecondary = {
  border: `1px solid ${WA_DARK}`,
  background: '#fff',
  color: WA_DARK,
  fontWeight: 800,
  fontSize: 13,
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
}

export default WhatsAppConnectionCard
